import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateTime(timestamp: string): string {
  return new Date(timestamp).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function getSeverityColor(severity: string): string {
  const colors: Record<string, string> = {
    critical: 'text-red-400',
    high: 'text-orange-400',
    medium: 'text-yellow-400',
    low: 'text-green-400',
    info: 'text-blue-400',
  };
  return colors[severity] || 'text-gray-400';
}

export function getSeverityBg(severity: string): string {
  const colors: Record<string, string> = {
    critical: 'bg-red-500/20 border-red-500/30',
    high: 'bg-orange-500/20 border-orange-500/30',
    medium: 'bg-yellow-500/20 border-yellow-500/30',
    low: 'bg-green-500/20 border-green-500/30',
    info: 'bg-blue-500/20 border-blue-500/30',
  };
  return colors[severity] || 'bg-gray-500/20 border-gray-500/30';
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    available: 'text-green-400',
    active: 'text-green-400',
    deployed: 'text-blue-400',
    dispatched: 'text-blue-400',
    en_route: 'text-cyan-400',
    on_scene: 'text-purple-400',
    pending: 'text-yellow-400',
    new: 'text-yellow-400',
    open: 'text-amber-400',
    supervision: 'text-cyan-400',
    acknowledged: 'text-yellow-400',
    maintenance: 'text-orange-400',
    resolved: 'text-gray-400',
    closed: 'text-gray-500',
    offline: 'text-red-400',
    cancelled: 'text-gray-500',
    on_break: 'text-purple-400',
  };
  return colors[status] || 'text-gray-400';
}

export function getStatusBg(status: string): string {
  const colors: Record<string, string> = {
    available: 'bg-green-500/20',
    active: 'bg-green-500/20',
    deployed: 'bg-blue-500/20',
    dispatched: 'bg-blue-500/20',
    en_route: 'bg-cyan-500/20',
    on_scene: 'bg-purple-500/20',
    pending: 'bg-yellow-500/20',
    new: 'bg-yellow-500/20',
    open: 'bg-amber-500/20',
    supervision: 'bg-cyan-500/20',
    maintenance: 'bg-orange-500/20',
    resolved: 'bg-gray-500/20',
    closed: 'bg-gray-500/20',
    offline: 'bg-red-500/20',
    on_break: 'bg-purple-500/20',
  };
  return colors[status] || 'bg-gray-500/20';
}

export function getCategoryIcon(category: string): string {
  const icons: Record<string, string> = {
    fire: '🔥',
    medical: '🏥',
    traffic: '🚗',
    criminal: '🚨',
    hazmat: '☢️',
    natural_disaster: '🌊',
    infrastructure: '🏗️',
    public_order: '👮',
    other: '📋',
  };
  return icons[category] || '📋';
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
export const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || '';
